import { useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { listPackageFiles, uploadPackageFile, deletePackageFile, bulkDeletePackageFiles, packageFileDownloadUrl, type PackageFileMeta, type LaborTenderIngestRow } from '../lib/api';

const KB = (n: number | null) => (n == null ? '' : n < 1024 ? `${n} B` : n < 1024 * 1024 ? `${Math.round(n / 1024)} KB` : `${(n / 1024 / 1024).toFixed(1)} MB`);
const CATS = [['drawing', '图纸'], ['spec', '规范'], ['standard', '质量/安全标准'], ['boq', '清单'], ['other', '其他']];

/** DR-B · staff (GM/estimate) upload + browse tender requirement files; also shows labor-uploaded docs.
 *  syncRows (optional) = this package's 采购大脑 ingest ledger — surfaces the last pull + staged/committed/error counts. */
export function PackageFilesPanel({ pid, syncRows }: { pid: string; syncRows?: LaborTenderIngestRow[] }) {
  const qc = useQueryClient();
  const { data: files } = useQuery<PackageFileMeta[]>({ queryKey: ['package-files', pid], queryFn: () => listPackageFiles(pid) });
  const inputRef = useRef<HTMLInputElement>(null);
  const [cat, setCat] = useState('drawing');
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [showSyncErr, setShowSyncErr] = useState(false);   // expand 采购大脑 pull error notes

  // Multi-select (改改 6/26): upload each picked file in turn so one failure (e.g. an over-limit file)
  // doesn't abort the rest — failures are collected and shown per-file.
  async function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []); if (!files.length) return;
    setBusy(true); setErr(null);
    const errs: string[] = [];
    for (let i = 0; i < files.length; i++) {
      setProgress(`${i + 1}/${files.length} · ${files[i].name}`);
      try { await uploadPackageFile(pid, files[i], cat); }
      catch (e) { errs.push(`${files[i].name}：${(e as any)?.body?.error ?? (e as any)?.message ?? '上传失败'}`); }
    }
    await qc.invalidateQueries({ queryKey: ['package-files', pid] });
    setErr(errs.length ? errs.join('\n') : null);
    setBusy(false); setProgress(null);
    if (inputRef.current) inputRef.current.value = '';
  }

  async function del(f: PackageFileMeta) {
    if (!confirm(`删除「${f.file_name}」？此操作不可撤销。/ Delete this file?`)) return;
    setErr(null);
    try { await deletePackageFile(pid, f.id); setSel((s) => { const n = new Set(s); n.delete(f.id); return n; }); await qc.invalidateQueries({ queryKey: ['package-files', pid] }); }
    catch (e) { setErr(String((e as any)?.message ?? '删除失败 / delete failed')); }
  }

  // Batch delete (改改 6/26: 不能批量删除) — select rows, delete in one call.
  const toggleSel = (id: string) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  async function bulkDeleteSelected() {
    if (!sel.size || !confirm(`删除所选 ${sel.size} 个文件？此操作不可撤销。/ Delete ${sel.size} selected files?`)) return;
    setBusy(true); setErr(null);
    try { await bulkDeletePackageFiles(pid, { ids: [...sel] }); setSel(new Set()); await qc.invalidateQueries({ queryKey: ['package-files', pid] }); }
    catch (e) { setErr(String((e as any)?.body?.error ?? (e as any)?.message ?? '删除失败 / delete failed')); }
    finally { setBusy(false); }
  }

  const reqs = (files ?? []).filter((f) => f.kind === 'requirement');
  const docs = (files ?? []).filter((f) => f.kind === 'labor_doc');
  const allSel = reqs.length > 0 && reqs.every((f) => sel.has(f.id));
  const toggleAll = () => setSel(allSel ? new Set() : new Set(reqs.map((f) => f.id)));

  // 采购大脑 sync visibility (spec §5): last pull + staged/committed/error counts, error notes on expand.
  const sync = syncRows ?? [];
  const syncCounts = { staged: 0, committed: 0, error: 0 };
  for (const r of sync) { if (r.status === 'staged') syncCounts.staged++; else if (r.status === 'committed') syncCounts.committed++; else if (r.status === 'error' || r.status === 'skipped_404') syncCounts.error++; }
  const lastPull = sync.reduce<string | null>((m, r) => (!m || r.last_seen_at > m ? r.last_seen_at : m), null);
  const syncErrRows = sync.filter((r) => (r.status === 'error' || r.status === 'skipped_404') && (r.error_note || r.file_name));

  return (
    <div className="card">
      <div className="row" style={{ gap: 8, alignItems: 'center', marginBottom: 8 }}>
        <strong>招标文件 / Tender requirements</strong>
        <span className="spacer" style={{ flex: 1 }} />
        <select value={cat} onChange={(e) => setCat(e.target.value)}>{CATS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
        <input ref={inputRef} type="file" multiple onChange={onPick} disabled={busy} />
        {busy && <span className="small muted">上传中 {progress} … / uploading</span>}
      </div>
      {sync.length > 0 && (
        <div className="small muted" style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8, paddingBottom: 6, borderBottom: '1px solid var(--rule)' }}>
          <span title="从预算部采购大脑自动拉取的 -AI 招标文件 / auto-pulled from 采购大脑">📥 采购大脑同步 / 采购大脑 sync</span>
          {lastPull && <span>· 最近拉取 {new Date(lastPull).toLocaleString('zh-CN', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' })} / last pull</span>}
          {syncCounts.staged > 0 && <span className="pill small pill-warn" title="已拉取待核对填充 / staged, awaiting 一键填充">待填充 {syncCounts.staged} / staged</span>}
          {syncCounts.committed > 0 && <span className="pill small pill-good" title="已填充进工程清单 / filled into the BOQ">已填充 {syncCounts.committed} / committed</span>}
          {syncCounts.error > 0 && (
            <button className="pill small pill-bad" title="展开错误说明 / show error notes" onClick={() => setShowSyncErr((v) => !v)} style={{ cursor: 'pointer', border: 'none' }}>
              异常 {syncCounts.error} / error {showSyncErr ? '▴' : '▾'}
            </button>
          )}
          {showSyncErr && syncErrRows.length > 0 && (
            <div style={{ flexBasis: '100%', marginTop: 2 }}>
              {syncErrRows.map((r) => (
                <div key={r.id} className="small" style={{ color: 'var(--bad)' }}>· {r.file_name ?? r.category_sub ?? r.id}{r.error_note ? `：${r.error_note}` : '（已移动或删除 / moved or deleted）'}</div>
              ))}
            </div>
          )}
        </div>
      )}
      {err && <div className="small" style={{ color: 'var(--bad)', whiteSpace: 'pre-line' }}>{err}</div>}
      {reqs.length === 0 && <div className="muted small">暂无文件 — 上传图纸、规范、质量/安全标准等。/ No files yet — upload drawings, specs, standards.</div>}
      {reqs.length > 0 && (
        <div className="row" style={{ gap: 8, alignItems: 'center', padding: '2px 0 6px' }}>
          <label className="small muted" style={{ cursor: 'pointer' }}><input type="checkbox" checked={allSel} onChange={toggleAll} style={{ marginRight: 4 }} />全选 / select all</label>
          {sel.size > 0 && <button className="small" disabled={busy} onClick={bulkDeleteSelected}>🗑 删除所选 ({sel.size}) / Delete selected</button>}
          <span className="spacer" style={{ flex: 1 }} />
        </div>
      )}
      {reqs.map((f) => (
        <div key={f.id} className="row-wrap" style={{ gap: 8, alignItems: 'baseline', padding: '3px 0', borderBottom: '1px solid var(--rule)' }}>
          <input type="checkbox" checked={sel.has(f.id)} onChange={() => toggleSel(f.id)} />
          {f.category && <span className="pill small pill-muted">{f.category}</span>}
          {f.source === 'wtl_pull' && <span className="pill small pill-stage" title="从采购大脑同步，仅内部可见（劳务方看不到）/ synced from brain, staff-only">采购大脑 · 仅内部</span>}
          <a href={packageFileDownloadUrl(pid, f.id)} style={{ flex: 1 }}>{f.file_name}</a>
          <span className="muted small">{KB(f.size_bytes)}{f.uploaded_by ? ` · ${f.uploaded_by}` : ''}</span>
          <button className="small" title="删除 / delete" disabled={busy} onClick={() => del(f)}>🗑</button>
        </div>
      ))}

      {docs.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <strong className="small">劳务上传的文件 / Labor-uploaded documents</strong>
          {docs.map((f) => (
            <div key={f.id} className="row-wrap" style={{ gap: 8, alignItems: 'baseline', padding: '3px 0' }}>
              <a href={packageFileDownloadUrl(pid, f.id)} style={{ flex: 1 }}>{f.file_name}</a>
              <span className="muted small">{KB(f.size_bytes)}{f.uploaded_by ? ` · ${f.uploaded_by}` : ''}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
